import { useMemo } from 'react'

import { useQuery } from 'react-query'

import { type RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import {
  type TermsMethodsKindEnum,
  TermsMethodsKinds,
} from '@webapp/modules/purchasing/entities/enums/TermsMethodsKindEnum'
import {
  PaymentTerm,
  type PaymentTermConstructor,
} from '@webapp/modules/vendor/entities/PaymentTerm' // eslint-disable-line import/no-internal-modules
import {
  ShippingMethod,
  type ShippingMethodConstructor,
} from '@webapp/modules/vendor/entities/ShippingMethod' // eslint-disable-line import/no-internal-modules
import {
  ShippingTerm,
  type ShippingTermConstructor,
} from '@webapp/modules/vendor/entities/ShippingTerm' // eslint-disable-line import/no-internal-modules
import {
  paymentTermQueryConfig,
  shippingMethodQueryConfig,
  shippingTermQueryConfig,
} from '@webapp/modules/vendor/query-configs' // eslint-disable-line import/no-internal-modules

export type TermOrMethod = ShippingTerm | PaymentTerm | ShippingMethod

type TermOrMethodConstructor =
  | ShippingTermConstructor
  | PaymentTermConstructor
  | ShippingMethodConstructor

export interface TermsMethodsListParams {
  page: number
  pageSize: number
}

// Kind-switch lives in the Model layer, not the Feature Set
const KIND_QUERY_CONFIGS = {
  [TermsMethodsKinds.SHIPPING_TERMS]: shippingTermQueryConfig,
  [TermsMethodsKinds.PAYMENT_TERMS]: paymentTermQueryConfig,
  [TermsMethodsKinds.SHIPPING_METHODS]: shippingMethodQueryConfig,
} as const

type UseQueryTermsAndMethodsReturn = {
  items: TermOrMethod[]
  pagination: {
    count: number
    numPages: number
    currentPage: number
  }
  isLoading: boolean
  isFetching: boolean
  error: RepoError | null
}

export const useQueryTermsAndMethods = (
  kind: TermsMethodsKindEnum,
  params: TermsMethodsListParams
): UseQueryTermsAndMethodsReturn => {
  const { data, isLoading, isFetching, error } = useQuery<
    RepoPaginatedResponse<TermOrMethodConstructor>,
    RepoError
  >(KIND_QUERY_CONFIGS[kind].list(params))

  const items = useMemo<TermOrMethod[]>(() => {
    const rows = data?.data ?? []

    switch (kind) {
      case TermsMethodsKinds.PAYMENT_TERMS:
        return rows.map((row) => new PaymentTerm(row))
      case TermsMethodsKinds.SHIPPING_METHODS:
        return rows.map((row) => new ShippingMethod(row))
      case TermsMethodsKinds.SHIPPING_TERMS:
      default:
        return rows.map((row) => new ShippingTerm(row))
    }
  }, [data, kind])

  return {
    items,
    pagination: {
      count: data?.pagination?.count ?? 0,
      numPages: data?.pagination?.numPages ?? 0,
      currentPage: data?.pagination?.page ?? params.page,
    },
    isLoading,
    isFetching,
    error: error ?? null,
  }
}
